import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, CircleHelp } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  ORG_SEARCH_GROUP_LABELS,
  ORG_SEARCH_GROUP_ORDER,
  ORG_SEARCH_PLACEHOLDER,
  ORG_SEARCH_SYNTAX,
  flattenOrgSearchHits,
} from "@/lib/orgSearchQuery";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const DEBOUNCE_MS = 200;

/**
 * Org-scoped omnibox in the shell header. Hidden on Social / no org.
 */
const SearchBar = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const isMobile = useIsMobile();
  const listboxId = useId();
  const [helpOpen, setHelpOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const reqGen = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const orgId =
    selectedOrgId && selectedOrgId !== "social" ? selectedOrgId : null;

  const flatHits = useMemo(
    () => flattenOrgSearchHits(result?.groups),
    [result]
  );

  const runSearch = useCallback(
    async (q) => {
      if (!orgId || !accessToken) return;
      const trimmed = String(q || "").trim();
      if (!trimmed) {
        setResult(null);
        setLoading(false);
        return;
      }
      const gen = ++reqGen.current;
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/search`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { q: trimmed, limit_per_type: 5 },
          }
        );
        if (gen !== reqGen.current) return;
        setResult(res.data || null);
        setActiveIndex(-1);
      } catch {
        if (gen !== reqGen.current) return;
        setResult(null);
      } finally {
        if (gen === reqGen.current) setLoading(false);
      }
    },
    [orgId, accessToken]
  );

  useEffect(() => {
    if (!orgId) {
      setQuery("");
      setResult(null);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      setResult(null);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      runSearch(trimmed);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, orgId, runSearch]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goHit = useCallback(
    (hit) => {
      if (!hit?.href) return;
      setOpen(false);
      navigate(hit.href);
    },
    [navigate]
  );

  const goResultsPage = useCallback(() => {
    const q = query.trim();
    if (!orgId || !q) return;
    setOpen(false);
    navigate(
      `/o/${encodeURIComponent(orgId)}/search?q=${encodeURIComponent(q)}`
    );
  }, [navigate, orgId, query]);

  const onSubmit = (e) => {
    e.preventDefault();
    const exact = result?.exact_match;
    if (exact?.href) {
      goHit(exact);
      return;
    }
    goResultsPage();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp") && flatHits.length) {
      setOpen(true);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => {
        const next = i + 1;
        return next >= flatHits.length ? 0 : next;
      });
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => {
        if (i <= 0) return flatHits.length - 1;
        return i - 1;
      });
      return;
    }
    if (e.key === "Enter" && activeIndex >= 0 && flatHits[activeIndex]) {
      e.preventDefault();
      goHit(flatHits[activeIndex]);
    }
  };

  if (!orgId) return null;

  let flatCursor = -1;

  return (
    <div ref={wrapRef} className="relative min-w-0 flex-1 max-w-xl mx-auto">
      <form onSubmit={onSubmit} className="flex w-full items-center gap-1.5">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={ORG_SEARCH_PLACEHOLDER}
            className="w-full pl-10"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            autoComplete="off"
          />
        </div>
        {isMobile ? (
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Search syntax help"
            onClick={() => setHelpOpen(true)}
          >
            <CircleHelp className="h-4 w-4" />
          </button>
        ) : (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label="Search syntax help"
                  onClick={() => setHelpOpen(true)}
                >
                  <CircleHelp className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="end"
                className="w-64 px-3 py-2.5"
              >
                <p className="mb-2 text-xs font-medium text-popover-foreground">
                  Narrow with a prefix
                </p>
                <ul className="space-y-1">
                  {ORG_SEARCH_SYNTAX.map((row) => (
                    <li
                      key={row.prefix}
                      className="flex items-baseline justify-between gap-3 text-xs"
                    >
                      <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-foreground">
                        {row.prefix}
                      </code>
                      <span className="min-w-0 text-right text-muted-foreground">
                        {row.meaning}
                      </span>
                    </li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </form>
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Search syntax</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Narrow results with a prefix:
          </p>
          <ul className="space-y-2">
            {ORG_SEARCH_SYNTAX.map((row) => (
              <li
                key={row.prefix}
                className="flex items-baseline justify-between gap-3 text-sm"
              >
                <code className="shrink-0 rounded bg-muted px-1.5 py-1 font-mono text-xs text-foreground">
                  {row.prefix}
                </code>
                <span className="min-w-0 text-right text-muted-foreground">
                  {row.meaning}
                </span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
      {open && query.trim() ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(70vh,28rem)] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          {loading && !result ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
          ) : null}
          {result
            ? ORG_SEARCH_GROUP_ORDER.map((key) => {
                const hits = result.groups?.[key];
                if (!Array.isArray(hits) || hits.length === 0) return null;
                return (
                  <div key={key} className="py-1">
                    <div className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {ORG_SEARCH_GROUP_LABELS[key] || key}
                    </div>
                    {hits.map((hit) => {
                      flatCursor += 1;
                      const idx = flatCursor;
                      return (
                        <button
                          key={`${hit.type}-${hit.id}`}
                          type="button"
                          role="option"
                          aria-selected={activeIndex === idx}
                          className={cn(
                            "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-accent",
                            activeIndex === idx && "bg-accent"
                          )}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => goHit(hit)}
                        >
                          <span className="font-medium truncate w-full">
                            {hit.title}
                          </span>
                          {hit.subtitle ? (
                            <span className="text-xs text-muted-foreground truncate w-full">
                              {hit.subtitle}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            : null}
          {!loading &&
          result &&
          flatHits.length === 0 &&
          query.trim() ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              No matches. Press Enter for the full search page.
            </p>
          ) : null}
          {query.trim() ? (
            <button
              type="button"
              className="w-full border-t border-border px-3 py-2 text-left text-sm text-primary hover:bg-accent"
              onClick={goResultsPage}
            >
              View all results for “{query.trim()}”
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SearchBar;
